import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Shield, Clock, ArrowRight, TrendingUp } from "lucide-react";
import api from "@/lib/api";

/**
 * AdminTrials — internal power-user page for extending trials.
 *
 * Access model (deliberate simplicity):
 *   - The page is served to any authenticated user (Protected route).
 *   - The actual mutation requires the caller to paste the ADMIN_SECRET
 *     from `backend/.env` into the field on this page. Wrong secret → 403
 *     from the backend, correct one → the extension is applied and the
 *     audit-log row is written server-side.
 *   - The secret is kept in component state only (never localStorage,
 *     never a cookie) so refreshing the tab clears it. This keeps the
 *     surface area minimal while still being useful for a small team.
 *
 * If we ever add role-based auth, this page would migrate to a plain
 * "click to extend" flow — but that's a larger change we don't need yet.
 */
export default function AdminTrials() {
  const [secret, setSecret] = useState("");
  const [userId, setUserId] = useState("");
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  // Look up the target user's trial state as the caller types so they can
  // sanity-check before extending. Silent-fail on 401/404 to keep the page
  // usable even without prior lookup permissions.
  useEffect(() => {
    if (!userId || userId.length < 4) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/admin/trial-lookup?user_id=${encodeURIComponent(userId)}`, {
          headers: secret ? { "X-Admin-Secret": secret } : undefined,
        });
        if (!cancelled) setPreview(data);
      } catch {
        if (!cancelled) setPreview(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [userId, secret]);

  const submit = async (e) => {
    e.preventDefault();
    if (!secret) return toast.error("Paste the admin secret first.");
    if (!userId) return toast.error("Enter a user_id.");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/auth/admin/extend-trial",
        { user_id: userId, days: Number(days) },
        { headers: { "X-Admin-Secret": secret } },
      );
      setResult(data);
      toast.success(`Extended ${userId} by ${days} days`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Extension failed";
      toast.error(msg);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-trials-page">
      <div className="label-eyebrow flex items-center gap-2" style={{ color: "var(--accent-warm)" }}>
        <Shield size={12}/> Internal · Trial admin
      </div>
      <h1 className="text-4xl sm:text-5xl font-medium tracking-tight mt-3" style={{ fontFamily: "var(--font-heading)" }}>
        Extend a user&rsquo;s trial
      </h1>
      <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-lg leading-relaxed">
        Paste your <code className="text-[var(--accent-primary)]">ADMIN_SECRET</code> and
        target a user by their <code>user_id</code>. Successful extensions are
        written to the <code>admin_extend_audit</code> collection; you can extend a
        given user up to 3 times per rolling 24h window.
      </p>

      <div className="mt-4">
        <Link to="/admin/metrics" className="btn-ghost inline-flex items-center gap-1" data-testid="link-admin-metrics">
          <TrendingUp size={14}/> Revenue &amp; subscriber overview <ArrowRight size={13}/>
        </Link>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-trials-form">
        <div>
          <label className="label-eyebrow block mb-2" htmlFor="admin-secret">Admin secret</label>
          <input
            id="admin-secret"
            type="password"
            autoComplete="off"
            spellCheck="false"
            className="field-input w-full font-mono text-sm"
            placeholder="Paste the ADMIN_SECRET value…"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid="admin-secret-input"
          />
        </div>

        <div>
          <label className="label-eyebrow block mb-2" htmlFor="admin-userid">User ID</label>
          <input
            id="admin-userid"
            type="text"
            spellCheck="false"
            className="field-input w-full font-mono text-sm"
            placeholder="user_abcdef123456"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            data-testid="admin-userid-input"
          />
          {preview && (
            <p className="mt-2 text-xs text-[var(--text-muted)]" data-testid="admin-preview">
              <Clock size={11} className="inline mr-1"/>
              Current trial ends: <code>{preview.trial_ends_at || "—"}</code>
              {typeof preview.days_remaining === "number" && ` · ${preview.days_remaining} day(s) remaining`}
            </p>
          )}
        </div>

        <div>
          <label className="label-eyebrow block mb-2" htmlFor="admin-days">Days to add (1–365)</label>
          <input
            id="admin-days"
            type="number"
            min="1"
            max="365"
            className="field-input w-40 font-mono text-sm"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            data-testid="admin-days-input"
          />
        </div>

        <button
          type="submit"
          className="btn-primary"
          data-lock-exempt="true"
          disabled={busy || !secret || !userId}
          data-testid="admin-extend-submit"
        >
          {busy ? "Extending…" : "Extend trial"} <ArrowRight size={14}/>
        </button>
      </form>

      {result && (
        <div
          className="mt-8 card-flat p-5 border-l-4"
          style={{ borderLeftColor: "var(--accent-primary)" }}
          data-testid="admin-extend-result"
        >
          <div className="label-eyebrow" style={{ color: "var(--accent-primary)" }}>Extension applied</div>
          <div className="mt-2 text-sm space-y-1">
            <div><span className="text-[var(--text-muted)]">User:</span> <code>{result.user_id}</code></div>
            <div><span className="text-[var(--text-muted)]">Extended by:</span> {result.extended_by_days} days</div>
            <div><span className="text-[var(--text-muted)]">Previous end:</span> <code>{result.previous_trial_ends_at || "—"}</code></div>
            <div><span className="text-[var(--text-muted)]">New end:</span> <code style={{ color: "var(--accent-primary)" }}>{result.trial_ends_at}</code></div>
          </div>
        </div>
      )}
    </div>
  );
}
